import React from 'react';
import { FaCircleXmark } from 'react-icons/fa6';

import {
  ClearFiltersButton,
  FilterActionCell,
  FilterCell,
  FilterCellContent,
  FilterCheckboxInput,
  FilterCheckboxWrapper,
  FilterControlWrapper,
  FilterInput,
  FilterRow,
  FilterSelect,
  InlineClearFiltersButton,
} from './styles';

import type { CustomTableDataHeader } from '.';

export type FilterFieldType =
  | 'text'
  | 'select'
  | 'number'
  | 'date'
  | 'checkbox'
  | 'booleanSelect';

interface FilterTableProps<T extends Record<string, unknown>> {
  headers: Array<CustomTableDataHeader<T>>;
  filters: Record<string, string>;
  onFilterChange: (key: keyof T & string, value: string) => void;
  onClearFilters: () => void;
  getUniqueOptions: (key: keyof T & string) => string[];
  showIconOffcanvas: boolean;
  placeholder: string;
}

const FilterTable = <T extends Record<string, unknown>>({
  headers,
  filters,
  onFilterChange,
  onClearFilters,
  getUniqueOptions,
  showIconOffcanvas,
  placeholder,
}: FilterTableProps<T>) => {
  if (headers.length === 0) {
    return null;
  }

  return (
    <FilterRow>
      {showIconOffcanvas && (
        <FilterActionCell>
          <ClearFiltersButton
            type="button"
            onClick={onClearFilters}
            aria-label="Limpar filtros"
            title="Limpar filtros"
          >
            <FaCircleXmark />
          </ClearFiltersButton>
        </FilterActionCell>
      )}
      {headers.map((header, index) => {
        const type = header.fieldType ?? 'text';
        const value = filters[header.key] ?? '';
        const shouldRenderControl = header.renderable !== false;
        const widthStyle = header.width
          ? { width: `${header.width}%` }
          : undefined;
        const isCheckboxChecked =
          type === 'checkbox'
            ? typeof filters[header.key] === 'undefined'
              ? header.filterDefaultValue === 'true'
              : filters[header.key] === 'true'
            : false;

        return (
          <FilterCell key={`filter-${header.key}`} style={widthStyle}>
            <FilterCellContent>
              {!showIconOffcanvas && index === 0 && (
                <InlineClearFiltersButton
                  type="button"
                  onClick={onClearFilters}
                  aria-label="Limpar filtros"
                  title="Limpar filtros"
                >
                  <FaCircleXmark />
                </InlineClearFiltersButton>
              )}
              {shouldRenderControl && (
                <FilterControlWrapper>
                  {type === 'select' && (
                    <FilterSelect
                      value={value}
                      onChange={(event) =>
                        onFilterChange(header.key, event.target.value)
                      }
                    >
                      <option value="">Todos</option>
                      {getUniqueOptions(header.key).map((option) => (
                        <option key={`${header.key}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </FilterSelect>
                  )}
                  {type === 'checkbox' && (
                    <FilterCheckboxWrapper>
                      <FilterCheckboxInput
                        type="checkbox"
                        checked={isCheckboxChecked}
                        onChange={(event) =>
                          onFilterChange(
                            header.key,
                            event.target.checked ? 'true' : 'false',
                          )
                        }
                      />
                    </FilterCheckboxWrapper>
                  )}
                  {type === 'booleanSelect' && (
                    <FilterSelect
                      value={value}
                      onChange={(event) =>
                        onFilterChange(header.key, event.target.value)
                      }
                    >
                      <option value="">Todos</option>
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </FilterSelect>
                  )}
                  {type !== 'select' &&
                    type !== 'checkbox' &&
                    type !== 'booleanSelect' && (
                      <FilterInput
                        type={
                          type === 'number'
                            ? 'number'
                            : type === 'date'
                              ? 'date'
                              : 'text'
                        }
                        min={type === 'number' ? 0 : undefined}
                        placeholder={type === 'text' ? placeholder : undefined}
                        value={value}
                        onChange={(event) =>
                          onFilterChange(header.key, event.target.value)
                        }
                      />
                    )}
                </FilterControlWrapper>
              )}
            </FilterCellContent>
          </FilterCell>
        );
      })}
    </FilterRow>
  );
};

export default FilterTable;
